import constants from 'config/constants';
import { matchPath } from 'react-router-dom';
import packageInfo from '../../../package.json';
import {
  ALLOWED_EVENT_PROPERTIES,
  EVENT_DEFINITIONS,
  TELEMETRY_SCHEMA_VERSION,
} from './catalog';

const TERMINAL_STORAGE_KEY = 'c1do1.telemetry.terminals.v1';
const CONTEXT_STORAGE_KEY = 'c1do1.telemetry.context.v1';
const SEQUENCE_STORAGE_KEY = 'c1do1.telemetry.sequence.v1';
const OPERATION_TTL_MS = 24 * 60 * 60 * 1000;
const COURSE_SCOPED_TASKS = new Set([
  'upload', 'video_play', 'evaluation_save', 'multiple_choice_submission', 'course_navigation',
]);
let fallbackSequence = 0;

const randomId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

const readJson = (key, fallback) => {
  try {
    return JSON.parse(window.sessionStorage.getItem(key)) || fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (key, value) => {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage and telemetry are both best effort.
  }
};

// Keep the specific telemetry paths ahead of the broader router prefixes.
// /home is a legacy destination, not a declared application route.
const ROUTE_TEMPLATES = [
  { path: '/lti/courses/selection', exact: true },
  { path: '/lti', exact: true },
  { path: '/home', exact: true },
  { path: '/courses/new', exact: true },
  { path: '/courses/error', exact: true },
  { path: '/courses/stageTest/:course_id/:stage_id', exact: true },
  { path: '/courses/:course_id/stages/:stage_id/feedback/:answer_id/training', exact: true },
  { path: '/courses/:course_id/stages/:stage_id/feedback/:answer_id', exact: true },
  { path: '/courses/:course_id/stages/:stage_id/edit', exact: true },
  { path: '/courses/:course_id/stages/:stage_id', exact: true },
  { path: '/courses/:course_id/edit/:step', exact: true },
  { path: '/courses/:course_id/edit', exact: true },
  { path: '/courses/:course_id' },
  { path: '/auth' },
  { path: '/my-courses' },
  { path: '/profile' },
];

export const routeTemplate = (pathname = window.location?.pathname || '/') => {
  const route = ROUTE_TEMPLATES.find(({ path, exact }) => matchPath(pathname, { path, exact }));
  return route?.path || '/other';
};

export const deviceType = (
  ua = window.navigator?.userAgent || '',
  maxTouchPoints = window.navigator?.maxTouchPoints || 0
) => {
  if (/tablet|ipad/i.test(ua)) return 'tablet';
  if (/macintosh/i.test(ua) && maxTouchPoints > 1) return 'tablet';
  if (/android(?!.*mobile)/i.test(ua)) return 'tablet';
  if (/mobile|iphone|android/i.test(ua)) return 'mobile';
  return 'desktop';
};

export const browserFamily = (userAgent = window.navigator?.userAgent || '') => {
  if (/edg(?:a|ios)?\//i.test(userAgent)) return 'edge';
  if (/opr\//i.test(userAgent)) return 'opera';
  if (/samsungbrowser\//i.test(userAgent)) return 'samsung_internet';
  if (/crios\//i.test(userAgent)) return 'chrome';
  if (/fxios\//i.test(userAgent)) return 'firefox';
  if (/firefox\//i.test(userAgent)) return 'firefox';
  if (/chrome\//i.test(userAgent)) return 'chrome';
  if (/safari\//i.test(userAgent)) return 'safari';
  return 'other';
};

const telemetryContext = () => readJson(CONTEXT_STORAGE_KEY, {});

export const setTelemetryContext = ({ institutionPseudonym } = {}) => {
  const next = typeof institutionPseudonym === 'string' && /^[0-9a-f]{16}$/.test(institutionPseudonym)
    ? { institution_pseudonym: String(institutionPseudonym) }
    : {};
  writeJson(CONTEXT_STORAGE_KEY, next);
};

const commonProperties = () => ({
  schema_version: TELEMETRY_SCHEMA_VERSION,
  release: process.env.REACT_APP_RELEASE || process.env.REACT_APP_COMMIT_SHA || packageInfo.version,
  route_template: routeTemplate(),
  lti_context: (() => {
    try {
      return window.localStorage.getItem(constants.HIDDEN_COMPONENT_ON_LTI) === 'YES'
        ? 'lti'
        : 'non_lti';
    } catch {
      return 'unknown';
    }
  })(),
  product_universe: constants.IS_DUOC_DESIGN ? 'duoc' : 'universe_a',
  device_type: deviceType(),
  browser_family: browserFamily(),
  ...telemetryContext(),
});

const sanitizeProperties = (properties = {}) => Object.entries(properties)
  .reduce((safe, [key, value]) => {
    if (!ALLOWED_EVENT_PROPERTIES.has(key) || value === undefined || value === null) return safe;
    if (typeof value === 'string' && /^[a-z][a-z0-9_]{0,39}$/.test(value)) {
      safe[key] = value;
    }
    return safe;
  }, {});

const terminalLedger = () => {
  const now = Date.now();
  const current = readJson(TERMINAL_STORAGE_KEY, {});
  return Object.entries(current).reduce((fresh, [id, timestamp]) => {
    if (now - timestamp < OPERATION_TTL_MS) fresh[id] = timestamp;
    return fresh;
  }, {});
};

const wasTerminated = (operationId) => Boolean(terminalLedger()[operationId]);

const markTerminated = (operationId) => {
  const ledger = terminalLedger();
  ledger[operationId] = Date.now();
  writeJson(TERMINAL_STORAGE_KEY, ledger);
};

const nextClientSequence = () => {
  try {
    const stored = Number(window.sessionStorage.getItem(SEQUENCE_STORAGE_KEY)) || 0;
    const next = Number.isSafeInteger(stored) && stored >= 0 && stored < Number.MAX_SAFE_INTEGER
      ? stored + 1
      : 1;
    window.sessionStorage.setItem(SEQUENCE_STORAGE_KEY, String(next));
    return next;
  } catch {
    fallbackSequence += 1;
    return fallbackSequence;
  }
};

const send = (name, payload) => {
  try {
    if (typeof window.__C1DO1_TELEMETRY__ === 'function') {
      window.__C1DO1_TELEMETRY__(name, payload);
    }
    if (typeof window.clarity === 'function') {
      window.clarity('set', 'product_universe', payload.product_universe);
      window.clarity('set', 'lti_context', payload.lti_context);
      window.clarity('event', name);
    }
  } catch {
    // Analytics must never affect the task being measured.
  }

  // Dispatch before a hard navigation can discard unsent work. Requests may
  // arrive out of order; the API correlates them using event occurrence data.
  let token = null;
  try {
    token = window.localStorage.getItem(constants.AUTH_TOKEN);
  } catch {
    // Anonymous failure events can still be delivered when storage is blocked.
  }

  try {
    if (typeof window.fetch !== 'function') return;
    const request = window.fetch(`${constants.API_URI}/telemetry/events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { authorization: token } : {}),
      },
      body: JSON.stringify({ event: { ...payload, name } }),
      keepalive: true,
    });
    Promise.resolve(request).catch(() => {});
  } catch {
    // Telemetry must not affect navigation or the measured task.
  }
};

const courseIdFromPath = () => {
  const pathname = window.location?.pathname || '';
  const match = matchPath(pathname, { path: '/courses/stageTest/:course_id/:stage_id', exact: true })
    || matchPath(pathname, { path: '/courses/:course_id' });
  return match?.params?.course_id;
};

const safeCourseId = (value) => {
  const id = String(value ?? '');
  return /^[1-9][0-9]{0,17}$/.test(id) ? id : null;
};

export const createOperation = (task, { operationId, attempt = 1, courseId } = {}) => {
  const resolvedCourseId = COURSE_SCOPED_TASKS.has(task)
    ? safeCourseId(courseId ?? courseIdFromPath())
    : null;
  return {
    id: operationId || randomId(),
    task,
    attempt,
    startedAt: Date.now(),
    ...(resolvedCourseId ? { courseId: resolvedCourseId } : {}),
  };
};

export const emitOperationEvent = (operation, name, properties = {}) => {
  const definition = EVENT_DEFINITIONS[name];
  if (!operation || !definition) return false;
  const [task, phase, terminal] = definition;
  if (task !== operation.task || (terminal && wasTerminated(operation.id))) return false;

  const occurredAt = Date.now();
  const payload = {
    ...commonProperties(),
    ...sanitizeProperties(properties),
    event_id: randomId(),
    correlation_id: operation.id,
    occurred_at: new Date(occurredAt).toISOString(),
    client_sequence: nextClientSequence(),
    task,
    phase,
    attempt: operation.attempt,
    duration_ms: Math.max(0, occurredAt - operation.startedAt),
    ...(COURSE_SCOPED_TASKS.has(task) && safeCourseId(operation.courseId)
      ? { course_id: safeCourseId(operation.courseId) }
      : {}),
  };

  send(name, payload);
  if (terminal) markTerminated(operation.id);
  return true;
};

export const persistOperation = (key, operation) => writeJson(`c1do1.telemetry.operation.${key}`, operation);

export const consumeOperation = (key) => {
  const storageKey = `c1do1.telemetry.operation.${key}`;
  const operation = readJson(storageKey, null);
  try {
    window.sessionStorage.removeItem(storageKey);
  } catch {
    // Best effort.
  }
  return operation;
};
